<template>
<div data-component="PhotoGallery.vue" class="photo-gallery-wrapper" :class="{ 'is-adding': IsAdding }">
	<div
		v-if="!Single" class="[ photo-gallery ]" @mousemove="OnDragMove" @mouseup="OnDragEnd" @touchmove="OnDragMove"
		@touchend="OnDragEnd"
	>
		<div :class="{ 'is-dragging': DraggingIndex }">
			<div v-if="IsAdding" class="p-3 text-center">
				<Loader/>
			</div>
			<div
				v-else ref="GalleryContainer" class="photo-gallery-items | flex items-center flex-wrap -m-1"
				:data-test="DataTest && `${DataTest}__all_photos`" @mouseup.stop="OnDragInsideEnd"
				@touchend.stop="OnDragInsideEndTouch"
			>
				<template v-for="(Photo, Index) in Photos" :key="Photo.date || Photo.url">
					<div
						v-if="!Photo.markedForDeletion" class="[ photo-gallery-item ] relative p-1"
						:style="{ flex: `0 0 calc(100% / ${PerLine})` }" @mousedown="OnDragStart(Index, $event)"
						@touchstart="OnDragStart(Index, $event)"
					>
						<template v-if="!Photo.isFile">
							<Loader v-if="Loaded && Index === 0" :Type="'ring'" :size="'small'"/>
							<div
								v-else class="bg-white bg-center bg-no-repeat bg-cover border rounded-lg aspect-square border-neutral-100"
								:style="{ backgroundImage: `url('${Photo.url}')` }" :data-test="DataTest && `${DataTest}__loaded_photo`"
								@click="OnClick(Index, $event)"
							>
								<div class="left-0 is-dragging-sequence is-dragging-put-before"/>
								<div class="right-0 is-dragging-sequence is-dragging-put-after"/>
								<button
									v-if="!Readonly" class="absolute top-0 right-0 p-2 text-white rounded-full bg-danger"
									style="background: rgba(0, 0, 0, 0.25);" :data-test="DataTest && `${DataTest}__loaded_photo__remove`"
									@touchstart.prevent.stop="Remove(Index)" @mousedown.prevent.stop @touchend.prevent.stop
									@click.prevent.stop="Remove(Index)"
								>
									<OIcon name="trash" size="1"/>
								</button>
							</div>
						</template>

						<div
							v-else class="bg-white bg-center bg-no-repeat bg-cover border rounded aspect-square border-neutral-100"
							@click="OnClick(Index, $event)"
						>
							<div class="absolute inset-0 flex flex-col items-center justify-center">
								<Loader v-if="Loaded && Index === 0" :Type="'ring'" :size="'small'"/>
								<template v-else>
									<OIcon :name="mapFileExtensionToIcon(Photo.fileExtension || '')" size="1.5"/>
									<div class="mt-3 text-xs ellipsis" style="max-width: calc(100% - 32px);">
										{{ Photo.originalName }}
									</div>
								</template>
							</div>
							<div class="left-0 is-dragging-sequence is-dragging-put-before"/>
							<div class="right-0 is-dragging-sequence is-dragging-put-after"/>
							<button
								v-if="!Readonly" class="absolute top-0 right-0 p-2 text-white rounded-full bg-danger"
								style="background: rgba(0, 0, 0, 0.25);" @mousedown.prevent.stop @touchend.prevent.stop
								@click.prevent.stop="Remove(Index)"
							>
								<OIcon name="trash" size="1"/>
							</button>
						</div>
					</div>
				</template>

				<label
					v-if="!Readonly && !HideAddButton && DragAndDroppable && Photos.length < 1" :for="UploadFieldName"
					class="drag-area | w-full px-2.5 py-2.5 rounded-lg border flex-col justify-center items-center gap-2.5 inline-flex"
					:class="{ '!text-[#0009] !border-[#0009]': IsDragging, '!border-danger': LocalValidationMessage !== '' || HasErrors }" @dragover.prevent @drop.prevent="OnDragAndDropInput"
					@dragenter="dragEnter" @dragleave="dragLeave" @click="$emit('addPhotosButtonClicked')"
				>
					<div class="flex items-center justify-center text-info">
						<OIcon class="mr-3 pt-[1px]" name="camera-photo"/><div class="text-sm">Dodaj zdjęcia</div>
					</div>
				</label>
				<label
					v-if="!Readonly && !HideAddButton && (!DragAndDroppable || (DragAndDroppable && Photos.length > 0))"
					:for="UploadFieldName" class="add-photo | relative p-1" :style="{ flex: `0 0 calc(100% / ${PerLine})` }"
					:data-test="DataTest && `${DataTest}__upload_photo`" @dragover.prevent @drop.prevent="OnDragAndDropInput"
				>
					<div class="bg-white bg-center bg-no-repeat bg-cover border rounded b aspect-square border-neutral-100">
						<div class="absolute inset-0 flex flex-col items-center justify-center">
							<template v-if="!AllowFiles">
								<OIcon name="photo-add" size="2" :data-test="DataTest && `${DataTest}__upload_photo_icon`"/>
							</template>
							<template v-else>
								<OIcon name="plus" size="2" :data-test="DataTest && `${DataTest}__upload_photo_icon`"/>
								<div class="mt-3">{{ AttachText }}</div>
							</template>
						</div>
					</div>
				</label>
			</div>
		</div>

		<div v-if="DraggingIndex" class="[ photo-gallery-dragging ]"/>
		<div v-if="DraggingImage" class="[ photo-gallery-dragging-overlay ]">
			<div
				class="bg-center bg-no-repeat bg-cover rounded shadow-md"
				:style="{ left: CurrentPoint[0] + 'px', top: CurrentPoint[1] + 'px', backgroundImage: `url(${DraggingImage.url})`, height: DraggingElementSize + 'px', width: DraggingElementSize + 'px' }"
			/>
		</div>

		<div class="[ form-input-validation ] text-danger text-body-xs mt-2">
			{{ LocalValidationMessage }}
		</div>
		<input
			:id="UploadFieldName" ref="ImageUploadField" type="file" :accept="AcceptType"
			:multiple="!Single && !AddOneFile" hidden @change="ImageUpload"
		>
	</div>

	<div v-else class="photo-gallery | is-single">
		<input :id="UploadFieldName" ref="ImageUploadField" type="file" :accept="AcceptType" hidden @change="ImageUpload">
		<div v-if="HideSinglePreview" class="flex items-center justify-center gap-2 mt-3">
			<button
				v-if="Photos.length" :data-test="DataTest && `${DataTest}__photo__button_remove`"
				class="mx-2 btn text-accent" @click.prevent.stop="RemoveSingle" v-text="'Usuń'"
			/>
			<label :for="UploadFieldName">
				<button
					:data-test="DataTest && `${DataTest}__photo__button_edit_add`"
					class="mx-2 btn btn-medium btn-outline-accent" @click.prevent.stop
					v-text="`${Photos.length ? 'Zmień' : 'Dodaj'} zdjęcie`"
				/>
			</label>
		</div>
		<div
			v-else :data-test="DataTest && `${DataTest}__photo`"
			class="flex flex-col items-center justify-center gap-4 rounded-lg lg:gap-10 lg:flex-row lg:py-10 lg:border border-neutral-200"
			@click="$refs['ImageUploadField'].click()"
		>
			<div v-if="Photos.length" class="relative w-40 h-40 border rounded-lg border-neutral-200">
				<img
					class="object-cover w-full h-full rounded-lg" :data-test="DataTest && `${DataTest}__image`"
					:src="Photos[0].url"
				>
				<button
					v-if="Photos.length" :data-test="DataTest && `${DataTest}__photo__button_remove`"
					class="absolute top-0 right-0 hidden w-6 h-6 m-1 text-black bg-white rounded-full lg:flex"
					@click.prevent.stop="RemoveSingle"
				>
					<OIcon name="close" :size="0.5"/>
				</button>
			</div>
			<BaseAvatar v-else-if="AvatarMode" :user="userStore" :hide-image="true" size="xl"/>
			<OIcon v-else name="photo" size="4" class="text-neutral-300"/>
			<div class="flex flex-col gap-4">
				<button
					ref="UploadButton" :data-test="DataTest && `${DataTest}__photo__button_edit_add`"
					class="btn btn-medium btn-outline-accent" @click.prevent.stop="$refs['ImageUploadField'].click()"
					v-text="`${Photos.length ? 'Zmień' : 'Dodaj'} zdjęcie`"
				/>
				<button
					v-if="Photos.length" :data-test="DataTest && `${DataTest}__photo__button_remove`"
					class="btn btn-medium text-accent lg:hidden" @click.prevent.stop="RemoveSingle" v-text="'Usuń'"
				/>
			</div>
		</div>
	</div>

	<ModalDialog
		class="photo-gallery-dialog is-dialog"
		:class="{ 'is-fullscreen': ScreenSize <= 1, 'is-photo-only': !onCustomerProfilePage }"
		:Visible="SelectedPhoto && (CurrentState.CropPhotoModal || Boolean($route.query['edycja-zdjecia'] === 'true'))"
	>
		<div
			v-if="!Single && Processing > -1"
			class="absolute inset-0 flex flex-col items-center justify-center bg-white z-64"
		>
			<Loader/>
			<div class="mb-5 heading-xs">
				Przetwarzanie {{ Processing }} z
				{{ Format.PluralForm(SelectedPhotoList.length, ['zdjęcia', 'zdjęć', 'zdjęć'], true) }}
			</div>
			<button class="btn btn-primary" @click.prevent="ProcessingCancel = true">
				Przerwij
			</button>
		</div>
		<button
			v-if="ScreenSize > 1" class="absolute top-0 right-0 p-3" :data-test="DataTest && `${DataTest}__modal__close`"
			@click.prevent="CloseModal"
		>
			<OIcon name="close" :size="1"/>
		</button>
		<template v-if="SelectedPhoto || SelectedPhotoList.length">
			<PageHeader
				:Title="!Single ? 'Dodaj zdjęcia' : 'Dodaj zdjęcie'" :Back="false"
				:Buttons="[{ Icon: 'close', Action: 'close' }]" @button-click="CloseModal"
			/>

			<div class="photo-gallery-container">
				<div class="flex">
					<div v-if="!Single" class="photo-gallery-list | flex px-7 py-3 mb-3 mt-5 bg-neutral-100">
						<div
							v-for="(Photo, i) in SelectedPhotoList" :key="Photo.Item.originalName" class="relative m-1 shrink-0"
							:class="{ 'is-selected': SelectedPhotoIndex === i }" @click="SelectedPhotoIndex = i"
						>
							<img :src="Photo.Item.url">
							<button
								v-if="ScreenSize > 1"
								class="photo-gallery-delete-photo | absolute right-0 top-0 text-white p-1 rounded-full"
								style="background: rgba(0, 0, 0, 0.25);" @click.prevent="RemovePhotoFromList(i)"
							>
								<OIcon name="trash" size="1"/>
							</button>
						</div>
						<div
							class="photo-gallery-list-add | flex-none flex items-center justify-center border border-neutral-100 rounded bg-white"
							:data-test="DataTest && `${DataTest}__modal__add_photo`" @click="ShowFileDialog(true)"
						>
							<OIcon name="plus" size="1"/>
						</div>
					</div>

					<div class="photo-gallery-panels">
						<div
							v-for="(Photo, i) in SelectedPhotoList" :key="Photo.Item.originalName"
							class="photo-gallery-panel | flex p-5" :class="{ 'is-selected': SelectedPhotoIndex === i }"
						>
							<PhotoEditor
								ref="PhotoEditor" v-model="Photo.Item" :AvatarMode="AvatarMode"
								:DataTest="`${DataTest}__modal`"
							/>
							<div v-if="onCustomerProfilePage">
								<div class="[ form-group ] is-editable">
									<div class="form-input form-multiline" :data-invalid="Boolean(Photo.ValidationMessage)">
										<label for="selected-photo-description">Opis</label>
										<textarea
											id="selected-photo-description" v-model="Photo.Description"
											:data-test="DataTest && `${DataTest}__modal__description`" @blur="DescriptionValidate()"
											@keypress="Photo.ValidationMessage = ''"
										/>
										<div class="[ form-input-validation ] text-danger text-xs mt-2">
											{{ Photo.ValidationMessage }}
										</div>
									</div>
								</div>
								<div class="mt-2 mb-3 text-neutral-600">
									Opis nie jest obowiązkowy. Jeśli go dodasz, pojawi się pod
									zdjęciem.
								</div>
								<button
									v-if="ScreenSize <= 1 && !Single && SelectedPhotoList.length > 1"
									class="photo-gallery-remove-photo | mt-5 btn btn-outline-black w-full"
									:data-test="DataTest && `${DataTest}__modal__remove_photo`"
									@click.prevent="RemovePhotoFromList(SelectedPhotoIndex)"
								>
									Usuń zdjęcie
								</button>
							</div>
						</div>
					</div>
				</div>

				<div v-if="ScreenSize > 1" class="flex items-center pb-7 px-7">
					<button
						v-if="SelectedPhoto && SelectedPhoto.Item.file && !UseCropWindow" class="btn-gray btn-small"
						@click.prevent="OnRotatePhoto"
					>
						Obróć
					</button>
					<hr class="flex-auto">
					<button
						class="btn-primary btn" :data-test="DataTest && `${DataTest}__modal__add_btn`"
						@click.prevent="OnGallerySaved"
					>
						Dodaj
						{{ SelectedPhotoList.length > 1 ? Format.PluralForm(SelectedPhotoList.length, ['zdjęcie', 'zdjęcia',
							'zdjęć'], true) : '' }}
					</button>
				</div>
				<div v-else class="sticky bottom-0 flex items-center flex-none p-3 mt-auto -mb-5 bg-white shadow-md z-48">
					<button
						v-if="SelectedPhoto && SelectedPhoto.Item.file && !UseCropWindow" class="mr-3 btn btn-gray"
						@click.prevent="OnRotatePhoto"
					>
						Obróć
					</button>
					<button
						class="w-full btn btn-primary" :data-test="DataTest && `${DataTest}__modal__add_btn`"
						@click.prevent="OnGallerySaved"
					>
						Dodaj
						{{ SelectedPhotoList.length > 1 ? Format.PluralForm(SelectedPhotoList.length, ['zdjęcie', 'zdjęcia',
							'zdjęć'], true) : '' }}
					</button>
				</div>
			</div>
		</template>
	</ModalDialog>
	<PromptDialog
		v-model="ConfirmModalVModel"
		title="Czy chcesz dodać wybrane zdjęcia?"
		dismiss-text="Nie, wyjdź bez dodawania"
		confirm-text="Tak, dodaj zdjęcia"
		@dismissed="CloseModalDismiss"
		@confirmed="CloseModalConfirm"
	/>
</div>
</template>

<script lang="ts">
import { mapState, mapStores } from 'pinia';
import { dataURLToBlob, sanitizeImage } from '@globals/utilities/ImageUtils';
import { useCustomerProfileViewStore } from '@customer/stores/customerProfileView';
import PhotoEditor from './PhotoEditor.vue';
import PromptDialog from './PromptDialog.vue';
import { useUserStore } from '@globals/stores/user';

// @vuese
// Komponent odpowiedzialny za wyświetlanie, zmianę kolejności i dodawanie grupy zdjęć.
// @group Base Components | Photos
export default {
	name: 'PhotoGallery',
	components: {
		PhotoEditor,
		PromptDialog,
	},
	props: {
		modelValue: {
			type: Array,
			default() {
				return [];
			},
		},
		Readonly: { type: Boolean, default: false },
		Single: { type: Boolean, default: false },
		AllowFiles: { type: Boolean, default: false },
		onCustomerProfilePage: { type: Boolean, default: false },
		HideAddButton: { type: Boolean, default: false },
		UploadFieldName: { type: String, default: 'upload-image' },
		Upload: {
			type: Object,
			default() {
				return { FieldPrefix: '' };
			},
		},
		MaxColumns: { type: Number, default: 4 },
		UseCropWindow: { type: Boolean, default: false },
		PreventSaveOnExit: { type: Boolean, default: false },
		AvatarMode: { type: Boolean, default: false },
		Draggable: { type: Boolean, default: false },
		Loaded: { type: Boolean, default: false },
		AddOneFile: { type: Boolean, default: false },
		AttachPerLine: { type: Number, default: 0 },
		AttachText: { type: String, default: 'Dodaj załącznik' },
		ForcePerLine: { type: Number },
		MaxFiles: { type: Number },
		ChatValidation: { type: Boolean, default: false },
		fileSizeValidation: { type: Number, default: 25 },
		attachmentValidation: { type: Boolean, default: false },
		HideSinglePreview: { type: Boolean, default: false },
		/**
		 * Nie usuwa usuniętych elementów z tablicy, lecz zamiast tego ustawia właściwość
		 * `markForDeletion` poszczególnego zdjęcia na `true`.
		 */
		markForDeletion: {
			type: Boolean,
			default: false,
		},
		DataTest: { type: String, default: undefined },
		DragAndDroppable: { type: Boolean, default: false },
	},
	emits: ['image-saved', 'upload', 'validationFail', 'onItemClick', 'click', 'removeItem', 'update:modelValue', 'addPhotosButtonClicked'],
	data() {
		return {
			Photos: [],
			Dragging: false,
			DraggingIndex: false,
			DraggingImage: '',
			DraggingElementSize: 0,
			StartPoint: [0, 0],
			CurrentPoint: [0, 0],
			LocalValidationMessage: '',
			SelectedPhoto: null,
			SelectedPhotoList: [],
			SelectedPhotoIndex: 0,
			Processing: -1,
			ProcessingCancel: false,
			IsAdding: false,
			DisableUpdateModel: false,
			DragCounter: 0,
			HasErrors: false,
			AppendMode: false,
		};
	},
	computed: {
		...mapStores(useUserStore),
		...mapState(useCustomerProfileViewStore, [
			'isVueRouterView',
		]),
		PerLine() {
			if (this.MaxColumns === 0) {
				switch (this.ScreenSize) {
					case 4: return 3;
					case 3: return 3;
					case 2: return 3;
					case 1: return 5;
					case 0: return 2;
				}
			}
			const r = this.ScreenSize > 0 ? 4 : 3;
			return this.AttachPerLine || this.ForcePerLine || Math.min(r, this.MaxColumns);
		},
		DraggingElement() {
			return this.$refs.GalleryContainer.children[this.DraggingIndex - 1];
		},
		AcceptType() {
			return this.AllowFiles ? '*/*' : 'image/*';
		},
		IsDragging() {
			return this.DragCounter > 0;
		},
		ConfirmModalVModel() {
			return this.isVueRouterView ? Boolean(this.$route.query['potwierdzanie-zdjecia'] === 'true') : this.CurrentState.CropPhotoConfirmModal;
		},
	},
	watch: {
		modelValue(n) {
			this.Photos = [...n];
		},
	},
	created() {
		this.Photos = JSON.parse(JSON.stringify(this.modelValue));
	},
	methods: {
		uploadSingleClicked() {
			this.$refs.ImageUploadField.click();
		},
		CloseModal() {
			if (this.isVueRouterView) {
				const query = {
					...this.$route.query,
					'potwierdzanie-zdjecia': this.UseCropWindow && !this.PreventSaveOnExit ? 'true' : 'false',
				};

				this.$router.push({ query });

				return;
			}

			if (this.UseCropWindow && !this.PreventSaveOnExit) {
				this.State.Append({ CropPhotoConfirmModal: true });
			} else {
				this.State.Append({ CropPhotoModal: false }, true);
			}
		},
		CloseModalConfirm(eventSource: string) {
			window.setTimeout(() => {
				this.OnGallerySaved();
			}, 200);
		},
		CloseModalDismiss(eventSource: string) {
			if (eventSource !== 'button') { return; }
			window.setTimeout(() => {
				this.ProcessingCancel = true;
				this.OnGallerySaved();
			}, 500);
		},
		DescriptionValidate() {
			let i = 0;
			for (const photo of this.SelectedPhotoList) {
				const description = String(photo.Description || '');
				const descCount = description.length;
				if (description && (description.indexOf('>') >= 0 || description.indexOf('<') >= 0)) {
					photo.ValidationMessage = 'Wprowadzony opis zawiera niedozwolone znaki.';
					return true;
				}

				if (descCount > 256) {
					photo.ValidationMessage = `Wprowadzony tekst jest za długi o ${this.Format.PluralForm(descCount - 256, ['znak', 'znaki', 'znaków'], true)}`;
					this.SelectedPhotoIndex = i;
					return true;
				}
				photo.ValidationMessage = '';

				i++;
			}
			return false;
		},

		RemovePhotoFromList(i) {
			if (this.SelectedPhotoIndex >= i) {
				this.SelectedPhotoIndex--;
			}
			this.SelectedPhotoList.splice(i, 1);
		},
		async OnGallerySaved() {
			const galleryDialogContent = document.querySelector('.photo-gallery-gallery .modal-dialog-content');
			if (galleryDialogContent && this.DescriptionValidate() && !this.ProcessingCancel) {
				galleryDialogContent.scrollTop = 512;
				return;
			}

			let copy = [...this.Photos];
			for (const j in this.SelectedPhotoList) {
				this.Processing++;
				if (this.ProcessingCancel) { continue; }
				const editor = this.$refs.PhotoEditor[j] || this.$refs.PhotoEditor;
				const p = this.SelectedPhotoList[j];
				const i = copy.findIndex((pp) => {
					if (pp.date) {
						return pp.date === p.Item.date;
					}
					return pp.url === p.Item.url;
				});
				copy[i].description = p.Description;
				copy[i].edited = true; // TODO: Save crop settings?

				const origFile = p.Item.file;
				const cropped = editor.GetCroppedImage();
				if (cropped) {
					const blob = editor.Edited ? { blob: dataURLToBlob(cropped) } : await sanitizeImage(origFile, 2048);
					const formData = this.CreateFormData(blob, `crop-${origFile.name}`);
					const rsvp = await this.SubmitFormData(formData.Form);
					copy[i].uploadedPath = rsvp.fileUploadName || rsvp.path;
					if (editor.Edited) {
						copy[i].cropped = false;
						if (cropped !== 'data:,') {
							copy[i].url = cropped;
						}
					}
				}

				copy[i].isProcessing = false;
			}

			copy = copy.filter((i) => !i.isProcessing);
			this.Set(copy);

			if (this.isVueRouterView) {
				const query = {
					'edycja-zdjecia': 'false',
				};

				this.$router.push({ query });
			} else {
				this.State.Travel(null, { ...this.CurrentState, CropPhotoModal: false }, 'fromHistory');
			}

			this.Set(copy);
			this.$emit('image-saved');

			this.Processing = -1;
			this.ProcessingCancel = false;
		},
		async OnRotatePhoto() {
			const editor = this.$refs.PhotoEditor;
			editor.RotatePhoto();
		},
		GoBack() {
			window.history.back();
		},
		ShowFileDialog(appendMode = false) {
			this.AppendMode = appendMode;
			this.$refs.ImageUploadField.click();
		},
		CreateFormData(u, name) {
			const d = new FormData();
			const originalType = u.blob.type || 'image/jpeg';
			const isImage = originalType.startsWith('image/');

			let fileName = (name || u.blob.name || '').replace(/[^a-zA-Z0-9\.]/g, '-').toLowerCase();

			if (isImage && !fileName.endsWith('.jpg') && !window.location.href.includes('/ekspert-oferteo')) {
				fileName += '.jpg';
			}

			const fieldPrefix = this.Upload.FieldPrefix ? `${this.Upload.FieldPrefix}.` : '';
			d.append(`${fieldPrefix}file`, u.blob, fileName);
			d.append(`${fieldPrefix}originalName`, fileName);
			d.append(`${fieldPrefix}ext`, fileName.split('.').pop() || '');
			d.append(`${fieldPrefix}contentType`, originalType);

			return {
				Name: fileName,
				Form: d,
			};
		},
		async SubmitFormData(data) {
			return new Promise(async (ok) => {
				if (this.Upload.Url) {
					fetch(this.Upload.Url, {
						method: 'POST', body: data, mode: 'cors', credentials: 'include',
					})
						.then((rsvp) => rsvp.json())
						.then((rsvp) => {
							try {
								rsvp = JSON.parse(rsvp);
								ok(rsvp);
							} catch (err) {
								ok({ err });
							}
						});
				} else {
					this.Set(this.Photos);
					ok({});
				}
			});
		},
		async ImageUpload(e: Event, skipAllowedExtensionsCheck = false) {
			const appendMode = this.AppendMode || this.CurrentState.CropPhotoModal;
			this.AppendMode = false;

			this.Processing = -1;
			this.LocalValidationMessage = '';

			const validation = {
				MaxFiles: false,
				Size: false,
				TotalSize: 0,
				AllowedExtensions: ['jpg', 'jpeg', 'png', 'pdf'],
				InvalidExtension: false,
			};

			this.Photos.forEach((u) => { validation.TotalSize += u.fileSize; });

			let multipleAdded;
			if (!appendMode) {
				this.SelectedPhotoList = [];
				this.SelectedPhotoIndex = 0;
				multipleAdded = false;
			} else {
				multipleAdded = true;
			}

			const files = e.target ? [...(e.target as HTMLInputElement).files] : [];
			const filesLength = files.length;
			const isMultiple = filesLength > 1 || appendMode;
			let loop = 0;
			let tooLargePhotos = 0;
			this.IsAdding = true;

			// eslint-disable-next-line no-restricted-syntax
			for (const f of files) {
				let skipFile = false;
				let validExtension = false;

				if (!f) { continue; }

				const fileSize = f.size / 1024 / 1024;

				if (this.onCustomerProfilePage && this.ScreenSize <= 1) {
					if (fileSize > 7) {
						tooLargePhotos++;
						continue;
					}
				}

				if (this.ChatValidation || this.attachmentValidation) {
					if (this.ChatValidation && (validation.TotalSize + fileSize) > this.fileSizeValidation) {
						validation.Size = true;
						skipFile = true;
					} else {
						validation.TotalSize += fileSize;
					}

					if (this.attachmentValidation && fileSize > this.fileSizeValidation) {
						validation.Size = true;
						skipFile = true;
					}

					for (const ext of validation.AllowedExtensions) {
						const lowerCaseName = f.name.toLowerCase();
						if (lowerCaseName.endsWith(ext)) {
							validExtension = true;
							break;
						}
					}

					if (!validExtension && !skipAllowedExtensionsCheck) {
						validation.InvalidExtension = true;
						skipFile = true;
					}
				}

				if (this.MaxFiles && this.Photos.length > (this.MaxFiles - 1)) {
					validation.MaxFiles = true;
					skipFile = true;
				}

				if (skipFile) { continue; }

				const isFile = !f.type.match(/image.*/);
				if (isFile && !this.AllowFiles && !isMultiple) {
					this.LocalValidationMessage = 'Wybrany format pliku nie jest obsługiwany. Wybierz plik w formacie JPG lub PNG.'; continue;
				}

				let u;
				if (!isFile) {
					u = await sanitizeImage(f, 0); // Czy to jest w ogóle potrzebne?
				} else {
					u = { data: f.name + f.size + f.lastModified, blob: f };
				}

				const formData = this.CreateFormData(u);
				u = {
					url: u.data,
					originalName: formData.Name,
					uploadedPath: '',
					isFile,
					file: f,
					cropped: false,
					markedForDeletion: false,
					date: (new Date()).toISOString(),
					isProcessing: true,
					fileSize,
					fileExtension: formData.Name.split('.').pop() || '',
				};

				if (!this.Single) {
					// eslint-disable-next-line no-restricted-syntax
					for (const p of this.Photos) {
						if (u.url === p.url) {
							if (!isMultiple) {
								if (this.ControllerComponent) {
									this.ControllerComponent.DataSaved = 'fail:gallery:duplicate';
								}
								this.$emit('validationFail', 'duplicate');
								this.$refs.ImageUploadField.value = '';
							}
							continue;
						}
					}
				}

				if (this.Single) { this.Photos = [u]; } else {
					this.Photos.splice(loop, 0, u);
					loop += 1;
				}
				this.Set(this.Photos);

				if (!this.UseCropWindow) {
					this.$emit('upload', u);
					const rsvp = await this.SubmitFormData(formData.Form);

					const item = this.Photos.find((i) => i.url === u.url);
					item.uploadedPath = rsvp.fileUploadName || rsvp.path;
				} else {
					const selectedPhoto = {
						Uploaded: true, Item: u, Description: u.description, ValidationMessage: '', IsProcessing: true,
					};
					this.SelectedPhotoList.push(selectedPhoto);
					multipleAdded = true;
				}

				this.Set(this.Photos);
			}

			if (tooLargePhotos > 0) {
				const allToLarge = tooLargePhotos === filesLength;
				this.Toast.Show({
					Message: allToLarge ? 'Wszystkie zdjęcia przekraczają rozmiar 7 MB' : 'Część zdjęć przekracza rozmiar 7 MB',
					Style: 'fail',
				});

				window.setTimeout(() => {
					this.IsAdding = false;
				}, 1000);

				if (allToLarge) {
					this.$refs.ImageUploadField.value = '';
					return;
				}
			}

			window.setTimeout(() => {
				this.IsAdding = false;
			}, 1000);

			if (this.ChatValidation || this.attachmentValidation) {
				const errors = [];
				this.HasErrors = false;
				if (validation.Size) {
					const attachmentMsg = `Plik nie może być większy niż ${this.fileSizeValidation} MB`;
					const chatMsg = `Wiadomość jest większa niż ${this.fileSizeValidation} MB i nie możemy jej wysłać. Spróbuj jeszcze raz, dodając załączniki osobno lub zmniejszając plik.`;
					if (this.attachmentValidation) {
						errors.push(attachmentMsg);
					} else {
						errors.push(chatMsg);
					}
				}
				if (validation.InvalidExtension) {
					errors.push(`${this.attachmentValidation ? 'Format pliku jest nieprawidłowy' : 'Niektóre pliki są nieprawidłowe i nie zostały dodane'}. ${this.AllowFiles ? 'Obsługiwane typy plików: JPG, PNG, PDF.' : 'Obsługiwane typy plików: JPG, JPEG, PNG.'}`);
				}
				if (errors.length > 0) {
					this.$emit('validationFail', errors);
					this.HasErrors = true;
				}
			}

			try {
				this.$refs.ImageUploadField.value = '';
			} catch (err) {
				console.warn('ImageUpladField: Clear Failed. ', err);
			}

			if (multipleAdded && !appendMode) {
				this.SelectedPhoto = this.SelectedPhotoList[0];

				if (this.isVueRouterView) {
					const query = {
						'edycja-zdjecia': 'true',
					};

					this.$router.push({ query });
				} else {
					this.State.Travel(null, { ...this.CurrentState, CropPhotoModal: true }, true);
				}
			}
		},
		OnClick(i) {
			this.$emit('onItemClick', i);

			if (this.Readonly) {
				return;
			}
			if (!this.Dragging) {
				if (!this.UseCropWindow) {
					this.$emit('click', this.Photos[i]);
				} else {
					this.SelectedPhoto = { Uploaded: false, Item: this.Photos[i], Description: this.Photos[i].description };
					this.SelectedPhotoList = [this.SelectedPhoto];

					if (this.isVueRouterView) {
						const query = {
							'edycja-zdjecia': 'true',
						};

						this.$router.push({ query });
					} else {
						this.State.Travel(null, { ...this.CurrentState, CropPhotoModal: true }, true);
					}
				}
			} else {
				this.DraggingIndex = null;
			}
		},
		OnDragStart(i, $e) {
			if ($e.touches) {
				$e = $e.touches[0];
			}
			if (this.Readonly) {
				return;
			}
			this.DraggingElementSize = Math.floor(this.$refs.GalleryContainer.clientWidth / this.PerLine);
			this.StartPoint = this.CurrentPoint = [$e.clientX, $e.clientY];
			this.DraggingIndex = i + 1;
		},
		OnDragMove($e) {
			if (!this.DraggingIndex) {
				return;
			}
			if ($e.touches) {
				$e = $e.touches[0];
			}
			this.CurrentPoint = [$e.clientX, $e.clientY];
			if (this.Format.Distance(this.StartPoint, this.CurrentPoint) > 2) {
				this.Dragging = this.DraggingIndex;
				if (!this.DraggingElement) {
					this.OnDragEnd();
					return;
				}
				this.DraggingElement.classList.add('is-being-dragged');
				this.DraggingImage = this.Photos[this.DraggingIndex - 1];
			}
		},
		OnDragInsideEndTouch() {
			const $target = document.elementFromPoint(this.CurrentPoint[0], this.CurrentPoint[1]);
			this.OnDragInsideEnd({ target: $target });
		},
		OnDragInsideEnd($e) {
			if (!this.DraggingIndex || !$e.target) {
				return;
			}
			if (this.Format.Distance(this.StartPoint, this.CurrentPoint) <= 2) {
				this.DraggingIndex = 0;
				this.OnDragEnd();
				return;
			}

			let index = this.Photos.length - 1;
			const $target = $e.target;
			if (!$target.matches('.photo-gallery-items')) {
				const $item = $target.closest('.photo-gallery-item');
				if (!$item) {
					this.OnDragEnd();
					return;
				}
				const $siblings = [].slice.call($item.parentElement.children);
				index = $siblings.findIndex((c) => c == $item); // eslint-disable-line eqeqeq
				if ($target.matches('.is-dragging-put-after')) {
					index++;
				}
			}
			const p = this.Photos;
			const t = p[this.DraggingIndex - 1];
			p.splice(this.DraggingIndex - 1, 1);
			p.splice(index, 0, t);
			this.Set(p);
			this.OnDragEnd();
		},
		OnDragEnd() {
			const el = this.$refs.GalleryContainer?.querySelector('.is-being-dragged');
			if (el) { el.classList.remove('is-being-dragged'); }
			this.Dragging = null;
			this.DraggingIndex = null;
			this.DraggingImage = null;
		},
		Remove(i) {
			if (this.Readonly) {
				return;
			}
			const p = this.Photos;
			this.$emit('removeItem', p[i]);

			if (this.markForDeletion) {
				p[i].markedForDeletion = true;
			} else {
				p.splice(i, 1);
			}
			this.Set(p);
		},
		RemoveSingle() {
			this.Set([]);
		},
		Set(list) {
			this.Photos = list;
			if (!this.IsAdding || !this.UseCropWindow) {
				this.$emit('update:modelValue', this.Photos);
			}
		},
		OnDragAndDropInput($e) {
			if (this.Readonly) {
				return;
			}
			const { files } = $e.dataTransfer;
			const inputEvent = new Event('input', { bubbles: true });
			Object.defineProperty(inputEvent, 'target', {
				writable: false,
				value: { files },
			});
			this.DragCounter = 0;
			this.ImageUpload(inputEvent);
		},
		dragEnter() {
			this.DragCounter++;
		},
		dragLeave() {
			this.DragCounter--;
		},
		mapFileExtensionToIcon(extension: string) {
			switch (extension) {
				case 'pdf':
					return 'attach';
				case 'webm':
					return 'volume-audio';
				default:
					return 'attach';
			}
		},
	},
};
</script>

<style lang="scss">
.photo-gallery * {
	cursor: pointer !important;
}

.photo-gallery .add-photo {
	order: -1;
}

.photo-gallery {
	background: white;
	user-select: none;

	input {
		display: none;
	}
}

.is-dragging > div {
	position: relative;
	z-index: 1025;
}

.photo-gallery-dragging-overlay,
.photo-gallery-dragging {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
}

.photo-gallery-dragging-overlay {
	pointer-events: none;
}

.photo-gallery-dragging-overlay > div {
	opacity: 0.75;
	pointer-events: none;
	transform: scale (0.9);
}

label[for="upload-image"] button {
	pointer-events: none;
}

.photo-gallery-dragging-overlay {
	z-index: 2048;
	pointer-events: none;

	>* {
		position: absolute;
		z-index: 1026;
		width: 4rem;
		height: 4rem;
		transform: translate(-50%, -50%);
	}
}

.is-being-dragged {
	opacity: 0.5;
	transition: opacity 0.15s ease-in;
}

.is-dragging {
	.photo-gallery-items label {
		pointer-events: none;
	}
}

.photo-gallery-items label {
	order: 1;
}

.is-dragging .is-dragging-sequence {
	position: absolute;
	z-index: 4096;
	top: 0;
	bottom: 0;
	width: 50%;
}

.photo-gallery-single-item {
	min-height: 160px;
}

.photo-gallery.is-single {
	background: transparent;

	label {
		order: 0 !important;
		margin: 0;
	}
}

.photo-gallery-hide > .photo-gallery {
	display: none !important;
}

.photo-gallery-panel.is-selected {
	z-index: 8;
}

.photo-gallery-panel {
	position: absolute;
	top: 0;
	right: 0;
	left: 0;
	background: #fff;
}

.photo-gallery-panel:first-child {
	position: relative;
}

.photo-gallery-container .photo-gallery-panels {
	position: relative;
	width: 100%;
}

.photo-gallery-panels {
	max-height: calc(100vh - 16rem);
	margin: 0 auto auto;
}

.photo-gallery-panels[data-v-789133] {}

.photo-gallery-list {}

.photo-gallery-list img {
	width: 72px;
	height: 72px;
	border: 2px solid transparent;
	border-radius: 4px;
	object-fit: cover;
	opacity: 0.75;
}

.photo-gallery-list div.is-selected img {
	box-shadow: 0 0 0 2px var(--accent);
	opacity: 1;
}

.photo-gallery-dialog.is-dialog .modal-dialog-content {
	max-width: 30rem;
}

.photo-gallery-container > div:first-child {
	overflow: hidden;
	max-height: calc(100vh - 16rem);
	flex-direction: column;
}

.photo-gallery-container > div:first-child > div {
	overflow: auto;
}

.is-mobile .photo-gallery-container > .flex {
	max-height: calc(100vh) !important;
}

.is-mobile .photo-gallery-panels {
	max-height: calc(100vh - 9rem);
}

.is-mobile .photo-gallery-container > .flex {
	max-height: calc(100vh - 9.125rem) !important;
	flex-direction: column;
}

.is-mobile .photo-gallery-dialog .sticky .btn {
	margin: 0 16px;
}

.is-mobile .photo-gallery-panels {
	width: 100%;
	max-width: 100%;
}

.photo-gallery-panel {
	flex-direction: row;
}

.photo-gallery-panel > div {
	flex: 0 0 50%;
	padding: 0 16px;
}

.photo-gallery-dialog .modal-dialog-content {
	max-width: 52rem !important;
}

.photo-gallery-list {
	flex: 0 0 auto;
	font-size: 0;
}

.photo-gallery-container > div > div {
	padding-right: 60px;
	padding-left: 60px;
}

.photo-gallery-container .photo-gallery-panels {
	position: relative;
	width: calc(100% - 64px);
	padding: 0;
	margin: 0 32px;
}

.is-desktop .photo-gallery-dialog .page-header .heading-sm {
	margin: 8px 40px;
	font-size: 24px;
}

.is-mobile .photo-gallery-panel {
	flex-direction: column;
}

.is-mobile .photo-gallery-container > .flex {
	max-height: calc(100vh - 9.125rem) !important;
	flex-direction: column-reverse;
}

.is-mobile .photo-gallery-container .photo-gallery-panels {
	width: 100%;
	margin: 0;
}

.is-mobile .photo-gallery-list {
	padding: 8px !important;
	margin: 0;
}

.is-mobile .photo-gallery-panel {
	padding: 20px 0;
}

.photo-gallery-list > div button i {
	transform: scale(0.75);
}

.photo-gallery-list > div button {
	display: none;
	margin: -4px;
}

.photo-gallery-list > div:hover button {
	display: block;
}

.photo-gallery-list > div {
	cursor: pointer;
}

.photo-gallery-list-add {
	width: 68px;
	margin: 6px 4px 6px 4px;
}

.photo-gallery-remove-photo {
	box-shadow: 0 0 0 1px var(--danger) !important;
	color: var(--danger) !important;
}

.photo-placeholder > div {
	border: 2px dashed var(--neutral-300);
	margin: -1px;
	color: var(--neutral-300);
}

.is-photo-only .modal-dialog-content {
	max-width: 32rem !important;
}

.is-photo-only .photo-gallery-panel > div {
	flex: 0 0 100%;
}

.is-photo-only .photo-gallery-container .photo-gallery-panels {
	width: 100%;
	margin: 0;
}

.is-desktop .photo-gallery-dialog.is-photo-only .page-header .heading-sm {
	margin-left: 16px;
}

.is-photo-only.is-fullscreen .photo-editor {
	flex-direction: column !important;
	margin: 0;
}

.is-photo-only.is-fullscreen .photo-editor-preview {
	min-height: calc(100vh - 240px) !important;
}

.is-mobile .is-photo-only .photo-gallery-panel {
	padding-bottom: 0;
}

.is-photo-only .photo-gallery-container .photo-gallery-list {
	padding-right: 40px;
	padding-left: 40px;
}

.is-mobile {
	.photo-gallery-confirm-dialog .flex.items-center {
		flex-direction: column-reverse;
	}

	.photo-gallery-confirm-dialog .flex.items-center button {
		width: 100%;
		margin: 4px 0;
	}

	.photo-gallery-confirm-dialog .heading-sm {
		font-size: 18px;
	}
}

.is-desktop .photo-gallery-list {
	max-height: 11rem;
	flex-wrap: wrap;
}

.single-empty-photo {
	display: flex;
	width: 112px !important;
	height: 112px !important;
	align-items: center;
	justify-content: center;
	background: var(--white);
	border-radius: 100%;
	box-shadow: inset 0 0 0 2px rgba(107, 107, 107, 25%);
}

.drag-area {
	border: 1px solid #00000026;
	color: var(--neutral-400);
	transition: all 0.15s ease-in;
}

.drag-area:focus,
.drag-area:hover {
	border-color: #0009;
	color: #0009;
}

@media (min-width: 960px) {
	.photo-gallery-delete-photo {
		opacity: 0;
	}

	.photo-gallery-item:hover .photo-gallery-delete-photo {
		opacity: 1;
	}
}

.photo-gallery-cover img {
	width: auto !important;
	height: 7rem !important;
	aspect-ratio: 760/200 !important;
	border-radius: 0 !important;
	object-fit: cover !important;
}
</style>
